import { Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, forkJoin, map, Observable, of, Subscription } from 'rxjs';
import { PlayerAuctionService } from '../player-auction.service';
import { BuyContractService } from '../buy-contract/buy-contract.service';

/**
 * Orders / Buy Contracts switch plus the page's refresh control. Lives in the
 * header's left slot so both pages put it in exactly the same spot, and shows
 * the running count of each side on its tab.
 */
@Component({
  selector: 'app-contract-page-nav',
  imports: [RouterLink, MatButtonModule, MatIconModule, MatTooltipModule],
  templateUrl: './contract-page-nav.html',
  styleUrl: './contract-page-nav.scss',
})
export class ContractPageNav implements OnChanges {
  @Input({ required: true }) current!: 'sell' | 'buy';
  @Input() lastUpdate = 0;
  @Input() refreshing = false;
  /** Bumped by the page on every reload so the tab counts refresh with it. */
  @Input() refreshTick = 0;
  @Output() refresh = new EventEmitter<void>();

  /** Transferring sell contracts; null until loaded or when the caller can't see them. */
  sellActive: number | null = null;
  /** Active buy contracts; null until loaded or when the caller can't see them. */
  buyActive: number | null = null;

  private sellService = inject(PlayerAuctionService);
  private buyService = inject(BuyContractService);
  private destroyRef = inject(DestroyRef);
  private countsSub?: Subscription;
  /** Sides the caller has no access to (401/403); never asked for again. */
  private forbidden = { sell: false, buy: false };

  ngOnChanges(changes: SimpleChanges): void {
    // Only a reload bumps refreshTick; lastUpdate/refreshing change every second and must not refetch.
    if (changes['refreshTick']) this.loadCounts();
  }

  private loadCounts(): void {
    this.countsSub?.unsubscribe();
    // Unfiltered on purpose: the tabs show what is running, whatever the page's filters are.
    this.countsSub = forkJoin({
      sell: this.count('sell', this.sellService.getSellContractOpenCounts().pipe(map(c => c['Transferring'] ?? 0))),
      buy: this.count('buy', this.buyService.getBuyContractOpenCounts().pipe(map(c => c['Active'] ?? 0))),
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ sell, buy }) => {
      this.sellActive = sell;
      this.buyActive = buy;
    });
  }

  private count(side: 'sell' | 'buy', request$: Observable<number>): Observable<number | null> {
    if (this.forbidden[side]) return of(null);
    return request$.pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 || err.status === 403) this.forbidden[side] = true;
        return of(null);
      }),
    );
  }
}
